import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { AuthModal } from '../components/AuthModal';
import { 
  Zap, 
  Bell, 
  Filter, 
  Shield, 
  ArrowRight,
  Check,
  Sun,
  Moon,
  Radio,
  Mail,
  Smartphone
} from 'lucide-react';
import { FaTelegram, FaGoogle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  {
    icon: FaTelegram,
    title: 'Connect Telegram',
    description: 'Securely link your Telegram account to monitor any public channel'
  },
  {
    icon: Filter,
    title: 'Keyword Filters',
    description: 'Set up smart filters to only get alerts for messages that matter'
  },
  {
    icon: Bell,
    title: 'Instant Alerts',
    description: 'Receive push, email or SMS notifications when a match is found'
  },
  {
    icon: Shield,
    title: 'Secure & Private',
    description: 'Your credentials are encrypted and sessions are stored securely'
  }
];

const pricingPlans = [
  {
    name: '30-Day Trial',
    price: '$0',
    period: 'for 30 days',
    features: ['2 channel monitors', '10 alerts/month', 'Email notifications', 'Basic keyword filters'],
    cta: 'Start Free Trial',
    popular: false
  },
  {
    name: 'Starter',
    price: '$19',
    period: '/month',
    features: ['5 channel monitors', '200 alerts/month', 'All notification types', 'Smart keyword matching', 'Priority support'],
    cta: 'Get Started',
    popular: true
  },
  {
    name: 'Pro',
    price: '$39',
    period: '/month',
    features: ['20 channel monitors', '500 alerts/month', 'All notification types', 'AI-powered matching', 'Dedicated support'],
    cta: 'Go Pro',
    popular: false
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loginWithGoogle, loginWithEmail } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Handle email verification and password reset from URL
  useEffect(() => {
    const verifyToken = searchParams.get('verify');
    const resetTokenParam = searchParams.get('reset');
    
    if (verifyToken) {
      verifyEmail(verifyToken);
    }
    
    if (resetTokenParam) {
      setResetToken(resetTokenParam);
      setShowAuthModal(true);
      // Clean up URL
      window.history.replaceState({}, '', '/');
    }
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { token });
      toast.success(response.data.message);
      setShowAuthModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    }
    // Remove token from URL
    window.history.replaceState({}, '', '/');
  };

  const handleLoginSuccess = (userData) => {
    loginWithEmail(userData);
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
      <div className="absolute top-0 right-0 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      
      {/* Navigation */}
      <nav className="relative z-10 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg md:text-xl font-['Outfit'] tracking-tight">
                Alert<span className="text-primary">afy</span>
              </span>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full w-9 h-9"
                data-testid="landing-theme-toggle"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              <Button 
                onClick={() => setShowAuthModal(true)}
                className="gap-2 rounded-full px-4 md:px-6 h-9 md:h-10 text-sm"
                data-testid="login-button"
              >
                <span className="hidden sm:inline">Sign in</span>
                <span className="sm:hidden">Login</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 pt-10 md:pt-20 pb-16 md:pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-medium mb-4 md:mb-8">
              <Radio className="w-3 h-3 md:w-4 md:h-4" />
              Real-time Telegram monitoring
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold font-['Outfit'] tracking-tight mb-6 md:mb-8 leading-tight">
              Never miss an important
              <span className="block gradient-text pb-2">Telegram message again</span>
            </h1>
            
            <p className="text-base md:text-xl text-muted-foreground mb-6 md:mb-10 max-w-2xl mx-auto px-2">
              Monitor Telegram channels with keyword filters and get instant alerts when matching posts appear.
            </p>
            
            <div className="flex flex-col items-center gap-4">
              <Button 
                size="lg" 
                onClick={() => setShowAuthModal(true)}
                className="gap-2 rounded-full px-6 md:px-8 h-11 md:h-12 text-sm md:text-base w-full sm:w-auto animate-pulse-glow"
                data-testid="hero-cta"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
              <div className="flex items-center gap-4 md:gap-6 text-xs md:text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-success" />
                  No credit card
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-success" />
                  2 free monitors
                </span>
              </div>
            </div>
          </motion.div>

          {/* Alert Preview */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-10 md:mt-20 max-w-3xl mx-auto"
          >
            <Card className="glass border-primary/20 overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-primary/5 px-4 md:px-6 py-3 md:py-4 border-b border-border/50 flex items-center justify-between">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <FaTelegram className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-xs md:text-sm">TechCrunch Channel</p>
                      <p className="text-[10px] md:text-xs text-muted-foreground">Keyword matched: &quot;AI&quot;, &quot;startup&quot;</p>
                    </div>
                  </div>
                  <span className="text-[10px] md:text-xs text-muted-foreground">Just now</span>
                </div>
                <div className="p-4 md:p-6">
                  <p className="text-xs md:text-sm leading-relaxed">
                    🚀 Breaking: New AI startup raises $50M Series A to revolutionize content monitoring. 
                    The platform uses advanced NLP to track social media channels in real-time...
                  </p>
                  <div className="flex items-center gap-3 md:gap-4 mt-3 md:mt-4 pt-3 md:pt-4 border-t border-border/50">
                    <div className="flex items-center gap-1.5 text-[10px] md:text-xs text-muted-foreground">
                      <Mail className="w-3 h-3 md:w-4 md:h-4 text-success" />
                      Email sent
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] md:text-xs text-muted-foreground">
                      <Bell className="w-3 h-3 md:w-4 md:h-4 text-success" />
                      Push sent
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-12 md:py-24 px-4 sm:px-6 lg:px-8 bg-card/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-3 md:mb-4">
              Everything you need to stay informed
            </h2>
            <p className="text-muted-foreground text-sm md:text-lg max-w-2xl mx-auto">
              Powerful features designed to help you monitor Telegram channels efficiently
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full card-hover border-border/50 bg-card/50">
                    <CardContent className="p-4 md:p-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3 md:mb-4">
                        <Icon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-sm md:text-lg mb-1 md:mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground text-xs md:text-sm leading-relaxed">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative z-10 py-12 md:py-24 px-4 sm:px-6 lg:px-8" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-3 md:mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-muted-foreground text-sm md:text-lg max-w-2xl mx-auto">
              Start free and scale as you grow. No hidden fees.
            </p>
          </div>

          {/* Mobile: Side by side grid, Desktop: 3 columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={plan.popular ? 'col-span-2 md:col-span-1 order-first md:order-none' : ''}
              >
                <Card className={`h-full relative ${plan.popular ? 'border-primary shadow-lg shadow-primary/20 bg-primary/10' : 'border-border bg-card/80'}`}>
                  {plan.popular && (
                    <div className="absolute -top-2.5 md:-top-3 left-1/2 -translate-x-1/2">
                      <span className="px-2 md:px-3 py-0.5 md:py-1 rounded-full bg-primary text-primary-foreground text-[10px] md:text-xs font-medium whitespace-nowrap">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <CardContent className="p-4 md:p-6">
                    <h3 className="font-bold text-base md:text-xl mb-1 md:mb-2">{plan.name}</h3>
                    <div className="flex items-baseline gap-0.5 md:gap-1 mb-4 md:mb-6">
                      <span className="text-2xl md:text-4xl font-bold font-['Outfit']">{plan.price}</span>
                      <span className="text-muted-foreground text-xs md:text-sm">{plan.period}</span>
                    </div>
                    <ul className="space-y-2 md:space-y-3 mb-4 md:mb-8">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-1.5 md:gap-2 text-xs md:text-sm">
                          <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-success flex-shrink-0 mt-0.5" />
                          <span className="leading-tight">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className="w-full rounded-full text-xs md:text-sm h-9 md:h-10" 
                      variant={plan.popular ? 'default' : 'outline'}
                      onClick={() => setShowAuthModal(true)}
                      data-testid={`pricing-${plan.name.toLowerCase()}`}
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-12 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <Card className="glass border-primary/20 p-6 md:p-12">
            <h2 className="text-2xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-3 md:mb-4">
              Ready to get started?
            </h2>
            <p className="text-muted-foreground text-sm md:text-lg mb-6 md:mb-8">
              Join thousands of users who trust Alertafy for their Telegram monitoring needs.
            </p>
            <Button 
              size="lg" 
              onClick={() => setShowAuthModal(true)}
              className="gap-2 rounded-full px-6 md:px-8 h-11 md:h-12 text-sm md:text-base"
              data-testid="final-cta"
            >
              Start Monitoring Now
            </Button>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border/50 py-6 md:py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs md:text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            <span>Alertafy</span>
          </div>
          <p>© 2025 Alertafy. All rights reserved.</p>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setResetToken(null);
        }}
        onGoogleLogin={loginWithGoogle}
        onLoginSuccess={handleLoginSuccess}
        resetToken={resetToken}
      />
    </div>
  );
}
